/**
 * Premium HTML email templates for ZBA Akademie booking system.
 * Customer confirmation + Admin alert emails.
 *
 * Design: Clean, confident, premium – matches ZBA brand identity.
 * Colors: ZBA Main (#3D4954), ZBA Blue (#D0E5F5), dark accents.
 */

const ZBA = {
  main:        '#3D4954',
  mainDark:    '#2d363d',
  mainDeep:    '#1e2830',
  blue:        '#D0E5F5',
  blueDark:    '#a8cce3',
  blueLight:   '#e8f2fa',
  green:       '#16a34a',
  greenLight:  '#dcfce7',
  greenBorder: '#86efac',
  black:       '#141414',
  white:       '#ffffff',
  grey50:      '#f8fafc',
  grey100:     '#f1f5f9',
  grey200:     '#e2e8f0',
  grey400:     '#94a3b8',
  grey500:     '#64748b',
  grey600:     '#475569',
  grey700:     '#334155',
};

const LOGO_URL = 'https://zba.academy/webinar/images/zbalogo3.png';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function getTypeLabel(type) {
  return type === 'onsite' ? 'Vor-Ort-Beratung (60 Min)' : 'Telefonberatung (30 Min)';
}

function getTypeIcon(type) {
  return type === 'onsite' ? '🏢' : '📞';
}

function getLocationLine(type, telefon) {
  if (type === 'onsite') {
    return `<tr>
      <td style="padding:10px 16px;color:${ZBA?.grey500};font-size:14px;width:44px;vertical-align:top;text-align:center;">📍</td>
      <td style="padding:10px 16px 10px 0;">
        <span style="display:block;font-size:12px;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.05em;margin-bottom:2px;">Ort</span>
        <span style="font-size:15px;color:${ZBA?.mainDeep};font-weight:600;">Kruppstraße 41-45, 42489 Wülfrath</span>
      </td>
    </tr>`;
  }
  return `<tr>
    <td style="padding:10px 16px;color:${ZBA?.grey500};font-size:14px;width:44px;vertical-align:top;text-align:center;">📞</td>
    <td style="padding:10px 16px 10px 0;">
      <span style="display:block;font-size:12px;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.05em;margin-bottom:2px;">Kontakt</span>
      <span style="font-size:15px;color:${ZBA?.mainDeep};font-weight:600;">Wir rufen dich unter +49 ${telefon || ''} an</span>
    </td>
  </tr>`;
}

/* ═══════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════ */

function emailWrapper(content) {
  return `<!DOCTYPE html>
<html lang="de" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="color-scheme" content="light">
  <meta name="supported-color-schemes" content="light">
  <title>ZBA Akademie</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        &lt;o:PixelsPerInch&gt;96&lt;/o:PixelsPerInch&gt;
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style>
    body, table, td { -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { -ms-interpolation-mode: bicubic; border: 0; outline: none; text-decoration: none; }
    @media only screen and (max-width: 620px) {
      .email-container { width: 100% !important; }
      .stack-column { display: block !important; width: 100% !important; }
      .pad-mobile { padding-left: 24px !important; padding-right: 24px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background:${ZBA?.grey100};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.grey100};">
    <tr>
      <td style="padding:48px 16px;" align="center">
        <table role="presentation" class="email-container" width="580" cellpadding="0" cellspacing="0" border="0" style="max-width:580px;width:100%;margin:0 auto;">
          ${content}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

function headerBlock(subtitle) {
  return `
          <!-- ═══ HEADER ═══ -->
          <tr>
            <td style="background:${ZBA?.mainDeep};border-radius:16px 16px 0 0;padding:0;">
              <div style="height:4px;background:linear-gradient(90deg, ${ZBA?.blue}, ${ZBA?.blueDark}, ${ZBA?.blue});border-radius:16px 16px 0 0;"></div>
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td class="pad-mobile" style="padding:32px 44px 28px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td>
                          <img src="${LOGO_URL}" alt="ZBA Akademie" width="140" style="display:block;max-width:140px;height:auto;border:0;" />
                        </td>
                        <td align="right" style="vertical-align:middle;">
                          <span style="display:inline-block;background:rgba(208,229,245,0.12);border:1px solid rgba(208,229,245,0.2);color:${ZBA?.blue};font-size:11px;font-weight:700;padding:6px 16px;border-radius:100px;text-transform:uppercase;letter-spacing:0.06em;">${subtitle}</span>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;
}

function footerBlock() {
  return `
          <!-- ═══ FOOTER ═══ -->
          <tr>
            <td style="padding:0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.grey50};border:1px solid ${ZBA?.grey200};border-top:none;border-radius:0 0 16px 16px;">
                <tr>
                  <td style="padding:28px 44px;text-align:center;">
                    <div style="height:1px;background:${ZBA?.grey200};margin-bottom:20px;"></div>
                    <p style="margin:0 0 6px;font-size:14px;font-weight:700;color:${ZBA?.main};">ZBA – Zentrale Brandschutztechnik-Akademie GmbH</p>
                    <p style="margin:0 0 4px;font-size:12px;color:${ZBA?.grey400};">Kruppstraße 41-45 · 42489 Wülfrath</p>
                    <p style="margin:0 0 4px;font-size:12px;color:${ZBA?.grey400};">
                      <a href="tel:+4920587792160" style="color:${ZBA?.grey500};text-decoration:none;">02058 / 77 921 60</a>
                      &nbsp;·&nbsp;
                      <a href="https://zba.academy" style="color:${ZBA?.main};text-decoration:none;font-weight:600;">zba.academy</a>
                    </p>
                    <div style="height:1px;background:${ZBA?.grey200};margin:16px 0;"></div>
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;">
                      <tr>
                        <td style="padding:0 6px;">
                          <img src="https://zba.academy/wp-content/uploads/Partner_Logos_AZAV_grau.png" alt="AZAV" width="36" height="36" style="display:block;opacity:0.5;border:0;" />
                        </td>
                        <td style="padding:0 6px;">
                          <img src="https://zba.academy/wp-content/uploads/Partner_Logos_Agentur_fuer_Arbeit.png" alt="Agentur für Arbeit" width="36" height="36" style="display:block;opacity:0.5;border:0;" />
                        </td>
                      </tr>
                    </table>
                    <p style="margin:12px 0 0;font-size:11px;color:${ZBA?.grey400};line-height:1.6;">
                      Diese E-Mail wurde automatisch durch das ZBA Buchungssystem versandt.<br/>
                      Deine Daten werden gemäß Art.&nbsp;6 Abs.&nbsp;1 lit.&nbsp;b DSGVO verarbeitet.
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;
}

/* ═══════════════════════════════════════════════
   CUSTOMER BOOKING CONFIRMATION
   ═══════════════════════════════════════════════ */

export function buildCustomerEmail(data) {
  const {
    vorname = '',
    nachname = '',
    email = '',
    telefon = '',
    type = 'phone',
    date = '',
    time = '',
    berater_name = '',
    booking_ref = '',
    outlook_link = '',
  } = data;

  const dateLabel = formatDate(date);
  const typeLabel = getTypeLabel(type);
  const typeIcon = getTypeIcon(type);
  const locationLine = getLocationLine(type, telefon);

  const content = `
          ${headerBlock('Terminbestätigung')}

          <!-- ═══ SUCCESS BANNER ═══ -->
          <tr>
            <td style="background:${ZBA?.green};padding:16px 44px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="text-align:center;">
                    <p style="margin:0;color:#fff;font-size:16px;font-weight:700;letter-spacing:0.01em;">✓&nbsp;&nbsp;Dein Beratungstermin ist bestätigt</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- ═══ BODY ═══ -->
          <tr>
            <td style="background:${ZBA?.white};">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td class="pad-mobile" style="padding:40px 44px 0;">
                    <p style="margin:0 0 6px;font-size:20px;color:${ZBA?.mainDeep};font-weight:700;">Hallo ${vorname},</p>
                    <p style="margin:0 0 32px;font-size:15px;color:${ZBA?.grey600};line-height:1.75;">
                      vielen Dank für deine Buchung! In deinem Beratungsgespräch klären wir gemeinsam, ob die Qualifizierung zum Sprinklertechniker das Richtige für dich ist.
                    </p>
                  </td>
                </tr>

                <!-- Appointment card -->
                <tr>
                  <td class="pad-mobile" style="padding:0 44px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.grey50};border:1px solid ${ZBA?.grey200};border-radius:14px;overflow:hidden;margin-bottom:32px;">
                      <!-- Card header -->
                      <tr>
                        <td colspan="2" style="background:${ZBA?.main};padding:14px 20px;">
                          <p style="margin:0;font-size:12px;font-weight:700;color:${ZBA?.blue};text-transform:uppercase;letter-spacing:0.08em;">Deine Termindetails</p>
                        </td>
                      </tr>
                      <!-- Type -->
                      <tr>
                        <td style="padding:14px 16px 0;color:${ZBA?.grey500};font-size:16px;width:44px;vertical-align:top;text-align:center;">${typeIcon}</td>
                        <td style="padding:14px 16px 0 0;">
                          <span style="display:block;font-size:12px;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.05em;margin-bottom:2px;">Art</span>
                          <span style="font-size:15px;color:${ZBA?.mainDeep};font-weight:600;">${typeLabel}</span>
                        </td>
                      </tr>
                      <!-- Divider -->
                      <tr>
                        <td colspan="2" style="padding:0 16px;"><div style="height:1px;background:${ZBA?.grey200};margin:10px 0;"></div></td>
                      </tr>
                      <!-- Date -->
                      <tr>
                        <td style="padding:10px 16px;color:${ZBA?.grey500};font-size:16px;width:44px;vertical-align:top;text-align:center;">📅</td>
                        <td style="padding:10px 16px 10px 0;">
                          <span style="display:block;font-size:12px;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.05em;margin-bottom:2px;">Datum</span>
                          <span style="font-size:15px;color:${ZBA?.mainDeep};font-weight:600;">${dateLabel}</span>
                        </td>
                      </tr>
                      <!-- Time -->
                      <tr>
                        <td style="padding:10px 16px;color:${ZBA?.grey500};font-size:16px;width:44px;vertical-align:top;text-align:center;">🕐</td>
                        <td style="padding:10px 16px 10px 0;">
                          <span style="display:block;font-size:12px;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.05em;margin-bottom:2px;">Uhrzeit</span>
                          <span style="font-size:15px;color:${ZBA?.mainDeep};font-weight:600;">${time} Uhr</span>
                        </td>
                      </tr>
                      <!-- Location / Phone -->
                      ${locationLine}
                      <!-- Berater -->
                      ${berater_name ? `
                      <tr>
                        <td style="padding:10px 16px;color:${ZBA?.grey500};font-size:16px;width:44px;vertical-align:top;text-align:center;">👤</td>
                        <td style="padding:10px 16px 10px 0;">
                          <span style="display:block;font-size:12px;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.05em;margin-bottom:2px;">Dein Berater</span>
                          <span style="font-size:15px;color:${ZBA?.mainDeep};font-weight:600;">${berater_name}</span>
                        </td>
                      </tr>` : ''}
                      <!-- Divider -->
                      <tr>
                        <td colspan="2" style="padding:0 16px;"><div style="height:1px;background:${ZBA?.grey200};margin:10px 0;"></div></td>
                      </tr>
                      <!-- Reference -->
                      <tr>
                        <td style="padding:10px 16px 16px;color:${ZBA?.grey500};font-size:16px;width:44px;vertical-align:top;text-align:center;">🔖</td>
                        <td style="padding:10px 16px 16px 0;">
                          <span style="display:block;font-size:11px;font-weight:700;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.08em;">Buchungsreferenz</span>
                          <span style="font-size:16px;font-weight:700;color:${ZBA?.mainDeep};font-family:'Courier New',Courier,monospace;letter-spacing:0.06em;">${booking_ref}</span>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                <!-- Outlook CTA -->
                ${outlook_link ? `
                <tr>
                  <td class="pad-mobile" style="padding:0 44px 28px;" align="center">
                    <!--[if mso]>
                    <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${outlook_link}" style="height:52px;v-text-anchor:middle;width:280px;" arcsize="23%" stroke="f" fillcolor="${ZBA?.mainDeep}">
                      <w:anchorlock/>
                      <center style="color:${ZBA?.blue};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:15px;font-weight:700;">📆&nbsp;&nbsp;Termin in Kalender speichern</center>
                    </v:roundrect>
                    <![endif]-->
                    <!--[if !mso]><!-->
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="border-radius:12px;background:${ZBA?.mainDeep};" align="center">
                          <a href="${outlook_link}" target="_blank" style="display:inline-block;padding:16px 36px;color:${ZBA?.blue};font-size:15px;font-weight:700;text-decoration:none;letter-spacing:0.01em;">
                            📆&nbsp;&nbsp;Termin in Kalender speichern
                          </a>
                        </td>
                      </tr>
                    </table>
                    <!--<![endif]-->
                  </td>
                </tr>` : ''}

                <!-- Info box -->
                <tr>
                  <td class="pad-mobile" style="padding:0 44px 32px;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.blueLight};border:1px solid ${ZBA?.blueDark};border-radius:12px;">
                      <tr>
                        <td style="padding:18px 22px;font-size:14px;color:${ZBA?.main};line-height:1.7;">
                          <strong>Was dich erwartet:</strong> Ein persönliches Gespräch, in dem wir dir die 6-monatige Qualifizierung zum Sprinklertechniker vorstellen. Wir besprechen Voraussetzungen, Ablauf und ob ein Bildungsgutschein für dich infrage kommt. Die Qualifizierung wird zu 100% über die Agentur für Arbeit oder das Jobcenter gefördert - für dich komplett kostenfrei.
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                <!-- Closing -->
                <tr>
                  <td class="pad-mobile" style="padding:0 44px 40px;">
                    <p style="margin:0 0 8px;font-size:15px;color:${ZBA?.grey600};line-height:1.7;">
                      Bei Fragen oder falls du den Termin verschieben möchtest, antworte einfach auf diese E-Mail oder ruf uns an.
                    </p>
                    <p style="margin:0;font-size:15px;color:${ZBA?.grey600};line-height:1.7;">
                      Wir freuen uns auf dich!
                    </p>
                    <p style="margin:20px 0 0;font-size:15px;color:${ZBA?.mainDeep};font-weight:600;">
                      Dein Team der ZBA Akademie
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          ${footerBlock()}`;

  return emailWrapper(content);
}

/* ═══════════════════════════════════════════════
   ADMIN ALERT EMAIL
   ═══════════════════════════════════════════════ */

export function buildAdminEmail(data) {
  const {
    vorname = '',
    nachname = '',
    email = '',
    telefon = '',
    wohnort = '',
    type = 'phone',
    date = '',
    time = '',
    berater_email = '',
    berater_name = '',
    webinar_id = '',
    source = '',
    booking_ref = '',
    outlook_link = '',
    utm_source = '',
    utm_medium = '',
    utm_campaign = '',
  } = data;

  const dateLabel = formatDate(date);
  const typeLabel = getTypeLabel(type);
  const typeIcon = getTypeIcon(type);

  const content = `
          ${headerBlock('Neue Buchung')}

          <!-- ═══ ALERT BANNER ═══ -->
          <tr>
            <td style="background:${ZBA?.main};padding:16px 44px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td>
                    <p style="margin:0;color:${ZBA?.white};font-size:16px;font-weight:700;">⚡&nbsp;&nbsp;Neue Terminbuchung eingegangen</p>
                  </td>
                  <td align="right">
                    <span style="display:inline-block;background:${ZBA?.green};color:#fff;font-size:11px;font-weight:700;padding:5px 14px;border-radius:100px;">${typeLabel}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- ═══ BODY ═══ -->
          <tr>
            <td style="background:${ZBA?.white};">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">

                <!-- Booking reference -->
                <tr>
                  <td class="pad-mobile" style="padding:32px 44px 0;">
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.grey50};border-left:4px solid ${ZBA?.main};border-radius:0 10px 10px 0;">
                      <tr>
                        <td style="padding:18px 22px;">
                          <p style="margin:0 0 4px;font-size:11px;font-weight:700;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.08em;">Buchungsreferenz</p>
                          <p style="margin:0;font-size:22px;font-weight:700;color:${ZBA?.mainDeep};font-family:'Courier New',Courier,monospace;letter-spacing:0.06em;">${booking_ref}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                <!-- Customer details -->
                <tr>
                  <td class="pad-mobile" style="padding:28px 44px 0;">
                    <p style="margin:0 0 12px;font-size:12px;font-weight:700;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.08em;">Kundendaten</p>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.grey50};border:1px solid ${ZBA?.grey200};border-radius:12px;overflow:hidden;">
                      <tr>
                        <td style="padding:14px 18px 0;color:${ZBA?.grey400};font-size:13px;width:120px;vertical-align:top;">Name</td>
                        <td style="padding:14px 18px 0;color:${ZBA?.mainDeep};font-size:15px;font-weight:600;">${vorname} ${nachname}</td>
                      </tr>
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">E-Mail</td>
                        <td style="padding:8px 18px 0;"><a href="mailto:${email}" style="color:${ZBA?.main};font-size:15px;font-weight:600;text-decoration:none;">${email}</a></td>
                      </tr>
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">Telefon</td>
                        <td style="padding:8px 18px 0;"><a href="tel:+49${telefon}" style="color:${ZBA?.main};font-size:15px;font-weight:600;text-decoration:none;">+49 ${telefon}</a></td>
                      </tr>
                      ${wohnort ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">Wohnort</td>
                        <td style="padding:8px 18px 0;color:${ZBA?.mainDeep};font-size:15px;font-weight:600;">${wohnort}</td>
                      </tr>` : ''}
                      ${webinar_id ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">Webinar-ID</td>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey500};font-size:14px;">${webinar_id}</td>
                      </tr>` : ''}
                      ${source ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">Quelle</td>
                        <td style="padding:8px 18px 0;"><span style="display:inline-block;background:${ZBA?.blueLight};color:${ZBA?.main};font-size:12px;font-weight:600;padding:3px 10px;border-radius:100px;">${source}</span></td>
                      </tr>` : ''}
                      ${utm_source ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">UTM-Quelle</td>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey500};font-size:14px;">${utm_source}</td>
                      </tr>` : ''}
                      ${utm_medium ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">UTM-Medium</td>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey500};font-size:14px;">${utm_medium}</td>
                      </tr>` : ''}
                      ${utm_campaign ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">UTM-Kampagne</td>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey500};font-size:14px;">${utm_campaign}</td>
                      </tr>` : ''}
                      <tr><td colspan="2" style="padding:10px 0 0;"></td></tr>
                    </table>
                  </td>
                </tr>

                <!-- Appointment details -->
                <tr>
                  <td class="pad-mobile" style="padding:24px 44px 0;">
                    <p style="margin:0 0 12px;font-size:12px;font-weight:700;color:${ZBA?.grey400};text-transform:uppercase;letter-spacing:0.08em;">Termindetails</p>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.grey50};border:1px solid ${ZBA?.grey200};border-radius:12px;overflow:hidden;">
                      <!-- Card header -->
                      <tr>
                        <td colspan="2" style="background:${ZBA?.main};padding:12px 18px;">
                          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                              <td style="color:${ZBA?.blue};font-size:16px;font-weight:700;">${dateLabel}</td>
                              <td align="right" style="color:${ZBA?.blue};font-size:16px;font-weight:700;">${time} Uhr</td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                      <tr>
                        <td style="padding:14px 18px 0;color:${ZBA?.grey400};font-size:13px;width:120px;vertical-align:top;">Art</td>
                        <td style="padding:14px 18px 0;color:${ZBA?.mainDeep};font-size:15px;font-weight:600;">${typeIcon} ${typeLabel}</td>
                      </tr>
                      ${type === 'onsite' ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">Ort</td>
                        <td style="padding:8px 18px 0;color:${ZBA?.mainDeep};font-size:15px;font-weight:600;">Kruppstraße 41-45, 42489 Wülfrath</td>
                      </tr>` : ''}
                      ${berater_name ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">Berater</td>
                        <td style="padding:8px 18px 0;color:${ZBA?.mainDeep};font-size:15px;font-weight:600;">${berater_name}</td>
                      </tr>` : ''}
                      ${berater_email ? `
                      <tr>
                        <td style="padding:8px 18px 0;color:${ZBA?.grey400};font-size:13px;vertical-align:top;">Berater-Mail</td>
                        <td style="padding:8px 18px 0;"><a href="mailto:${berater_email}" style="color:${ZBA?.main};font-size:14px;text-decoration:none;">${berater_email}</a></td>
                      </tr>` : ''}
                      <tr><td colspan="2" style="padding:10px 0 0;"></td></tr>
                    </table>
                  </td>
                </tr>

                <!-- Quick actions -->
                <tr>
                  <td class="pad-mobile" style="padding:28px 44px 36px;" align="center">
                    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        ${outlook_link ? `
                        <td style="padding:0 6px;">
                          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                              <td style="border-radius:10px;background:${ZBA?.mainDeep};" align="center">
                                <a href="${outlook_link}" target="_blank" style="display:inline-block;padding:14px 24px;color:${ZBA?.blue};font-size:14px;font-weight:700;text-decoration:none;">📆 Outlook</a>
                              </td>
                            </tr>
                          </table>
                        </td>` : ''}
                        <td style="padding:0 6px;">
                          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                              <td style="border-radius:10px;background:${ZBA?.green};" align="center">
                                <a href="tel:+49${telefon}" style="display:inline-block;padding:14px 24px;color:#fff;font-size:14px;font-weight:700;text-decoration:none;">📞 Anrufen</a>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <td style="padding:0 6px;">
                          <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                            <tr>
                              <td style="border-radius:10px;background:${ZBA?.grey100};border:1px solid ${ZBA?.grey200};" align="center">
                                <a href="mailto:${email}" style="display:inline-block;padding:14px 24px;color:${ZBA?.main};font-size:14px;font-weight:700;text-decoration:none;">✉️ E-Mail</a>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

              </table>
            </td>
          </tr>

          <!-- ═══ FOOTER (simplified for admin) ═══ -->
          <tr>
            <td style="padding:0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${ZBA?.grey50};border:1px solid ${ZBA?.grey200};border-top:none;border-radius:0 0 16px 16px;">
                <tr>
                  <td style="padding:18px 44px;text-align:center;">
                    <p style="margin:0;font-size:12px;color:${ZBA?.grey400};">
                      ZBA Buchungssystem · <a href="https://zba.academy" style="color:${ZBA?.main};text-decoration:none;font-weight:600;">zba.academy</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;

  return emailWrapper(content);
}
